import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// form
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import { TextField } from '@mui/material';

// ----------------------------------------------------------------------

// Máscara decimal pt-BR que cresce da esquerda: quem digita "30" quer 30, e os centavos entram
// quando o campo perde o foco ("30" → "30,00"). O RHFCurrencyTextField não serve para isso: a
// máscara dele é de caixa registradora, e o mesmo "30" vira 0,30.
const DECIMAL_PLACES = 2;

function toDecimalValue(value) {
  if (value === '' || value === null || value === undefined) return '';

  const number = Number(value);

  return Number.isFinite(number) ? number : '';
}

/**
 * Texto que o campo aceita: dígitos e uma vírgula, com até duas casas depois dela. Sem vírgula, o
 * ponto vira vírgula (teclado numérico, "30.5"); com vírgula, o ponto é separador de milhar e sai
 * ("1.234,56" colado). Devolve `null` para a digitação que não cabe, como uma segunda vírgula.
 */
export function sanitizeDecimalInput(raw) {
  let text = String(raw ?? '').replace(/[^\d,.]/g, '');

  text = text.includes(',') ? text.replace(/\./g, '') : text.replace(/\./g, ',');

  const [integer, ...decimals] = text.split(',');

  if (decimals.length > 1) return null;
  if (!decimals.length) return integer;

  return `${integer},${decimals[0].slice(0, DECIMAL_PLACES)}`;
}

/** Número do texto da máscara; campo vazio (ou só a vírgula) vai ao form como `''`. */
export function parseDecimalInput(text) {
  if (!text || text === ',') return '';

  const number = Number(text.replace(',', '.'));

  return Number.isFinite(number) ? number : '';
}

/** Texto da máscara para um valor do form: 30.5 → "30,50". */
export function formatDecimalInput(value) {
  const number = toDecimalValue(value);

  if (number === '') return '';

  return number.toFixed(DECIMAL_PLACES).replace('.', ',');
}

// ----------------------------------------------------------------------

DecimalTextField.propTypes = {
  field: PropTypes.object.isRequired,
  error: PropTypes.object,
  helperText: PropTypes.node,
  inputProps: PropTypes.object,
  onBlur: PropTypes.func,
};

function DecimalTextField({ field, error, helperText, inputProps, onBlur, ...other }) {
  const [text, setText] = useState(() => formatDecimalInput(field.value));
  // O que o próprio campo entregou ao form por último. Valor diferente disso foi escrito de fora
  // (reset da edição, descarte de alterações) e reescreve o texto; o que o campo mesmo entregou
  // não volta formatado, senão o "30," de quem vai digitar "30,5" viraria "30,00" no meio.
  const emittedRef = useRef(toDecimalValue(field.value));

  useEffect(() => {
    const value = toDecimalValue(field.value);

    if (value === emittedRef.current) return;

    emittedRef.current = value;
    setText(formatDecimalInput(value));
  }, [field.value]);

  const handleChange = (event) => {
    const nextText = sanitizeDecimalInput(event.target.value);

    if (nextText === null || nextText === text) return;

    const value = parseDecimalInput(nextText);

    setText(nextText);
    emittedRef.current = value;
    field.onChange(value);
  };

  const handleBlur = (event) => {
    setText(formatDecimalInput(parseDecimalInput(text)));
    field.onBlur();
    onBlur?.(event);
  };

  return (
    <TextField
      fullWidth
      {...other}
      name={field.name}
      inputRef={field.ref}
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!error}
      helperText={error ? error.message : helperText}
      inputProps={{ inputMode: 'decimal', ...inputProps }}
    />
  );
}

// ----------------------------------------------------------------------

RHFDecimalTextField.propTypes = {
  name: PropTypes.string.isRequired,
  helperText: PropTypes.node,
};

/** Campo decimal com máscara pt-BR; o form recebe número (30.5), nunca o texto formatado. */
export default function RHFDecimalTextField({ name, ...other }) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <DecimalTextField field={field} error={error} {...other} />
      )}
    />
  );
}
